import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  Container,
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Title,
  Text,
  Alert,
  Group,
  Box,
  Image,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { authApi } from '../api/auth';

interface SignupErrorResponse {
  code?: string;
  error?: string;
}

export default function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [volunteerNotFound, setVolunteerNotFound] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setVolunteerNotFound(false);
    setLoading(true);

    try {
      await authApi.signup({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      setSuccess(true);
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      const response = isAxiosError<SignupErrorResponse>(err)
        ? err.response
        : undefined;
      const isVolunteerNotFound =
        response?.data?.code === 'VOLUNTEER_NOT_FOUND' ||
        response?.status === 404;

      setVolunteerNotFound(isVolunteerNotFound);
      setError(
        isVolunteerNotFound
          ? t('signup.volunteerNotFound')
          : response?.data?.error ||
              (err instanceof Error ? err.message : t('signup.genericError'))
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F5F7FB',
          padding: '20px',
        }}
      >
        <Container size="sm">
          <Paper radius="md" p="xl" withBorder style={{ textAlign: 'center' }}>
            <Group justify="center" mb="lg">
              <IconCheck size={48} color="green" />
            </Group>
            <Title order={2} mb="md">
              {t('signup.success_title', 'Account Created')}
            </Title>
            <Text color="dimmed" mb="lg">
              {t(
                'signup.success_message',
                'Your account has been created successfully. Redirecting to login...'
              )}
            </Text>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F5F7FB',
        padding: '20px',
      }}
    >
      <Container size="sm">
        <Paper radius="md" p="xl" withBorder>
          {/* Logo */}
          <Group justify="center" mb="lg">
            <Image
              src="/strassenwaechter-logo.png"
              alt="Straßenwächter e.V."
              height={80}
              fit="contain"
            />
          </Group>

          <Title order={2} ta="center" mb="lg">
            {t('signup.title', 'Create Account')}
          </Title>

          <Text color="dimmed" ta="center" mb="lg" size="sm">
            {t('signup.subtitle', 'Sign up to become a volunteer')}
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle />} color="red" mb="lg">
              <Stack gap="sm">
                <Text>{error}</Text>
                {volunteerNotFound && (
                  <>
                    <Text size="sm">{t('signup.registerInsteadHint')}</Text>
                    <Button
                      component={Link}
                      to="/volunteer/register"
                      variant="light"
                      color="blue"
                    >
                      {t('signup.registerInstead')}
                    </Button>
                  </>
                )}
              </Stack>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label={t('signup.firstName', 'First Name')}
                placeholder={t(
                  'signup.firstName_placeholder',
                  'Enter your first name'
                )}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={loading}
              />

              <TextInput
                label={t('signup.lastName', 'Last Name')}
                placeholder={t(
                  'signup.lastName_placeholder',
                  'Enter your last name'
                )}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={loading}
              />

              <TextInput
                label={t('signup.email', 'Email')}
                type="email"
                placeholder={t('signup.email_placeholder', 'your@email.com')}
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />

              <PasswordInput
                label={t('signup.password', 'Password')}
                placeholder={t(
                  'signup.password_placeholder',
                  'Enter your password'
                )}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
              />

              <Button type="submit" fullWidth loading={loading} color="blue">
                {t('signup.button', 'Create Account')}
              </Button>
            </Stack>
          </form>

          <Text ta="center" mt="lg" size="sm">
            {t('signup.login_link', 'Already have an account?')}{' '}
            <Link to="/" style={{ color: '#1971C2', textDecoration: 'none' }}>
              {t('signup.login', 'Log in')}
            </Link>
          </Text>
          <Text ta="center" mt="xs" size="sm">
            <Link
              to="/forgot-password"
              style={{ color: '#1971C2', textDecoration: 'none' }}
            >
              {t('signup.forgot_password', 'Forgot password?')}
            </Link>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
}
